import { Stack } from '@servicetitan/design-system';
import CustomerDetails from '../CustomerDetails';
import MessageView from '../MessageView';
import styles from './TaskView.module.css';
import TaskCustomerSearch from '@components/CustomerSearch/TaskCustomerSearch';

import useTasks from '@modules/TasksModule/hooks/useTasks';
import TasksModule from '@modules/TasksModule';
import classNames from 'classnames';
import { useCallback } from 'react';
import TaskLoadingOverlay from '@app/layouts/MainLayout/components/TaskLoadingOverlay/TaskLoadingOverlay';

type Props = {
  taskId: string;
  taskTenantId: string;
  show?: boolean;
};

/**
 * This component is used to display the customer search and customer details for a task that has a tenantId
 *
 * @param taskId
 * @param taskTenantId - This is to explicitly pass the tenantId of the task. As this component should not be used without a tenantId.
 * @param show - Whether to show the component. Used to show/hide the component and preserve state.
 */
const TaskView = ({ taskId, show = false, taskTenantId }: Props) => {
  const { taskById } = useTasks();
  const task = taskById[taskId];

  const handleBackClick = useCallback(() => {
    TasksModule.setCustomerOnTask(taskId, undefined);
  }, [taskId]);

  // if the task is not found, in general we should have a task.
  if (!task) {
    return <div>Task not found</div>;
  }

  const isMessageSession = task.type === 'text';

  // we are using 'show' here to toggle the 'display' of the component
  // so it doesn't mount/unmount every time it's selected, because it
  // will lose any state
  return (
    <Stack
      className={classNames('h-100', 'flex-grow-1', {
        'd-b': show,
        [styles.TaskViewShown]: show,
        [styles.TaskViewHidden]: !show,
      })}
    >
      <TaskLoadingOverlay taskId={taskId}>
        {isMessageSession && (
          <MessageView
            phoneNumber={task.from}
            tenantId={taskTenantId}
            customerId={task.attributes.customerId}
          />
        )}
        <div className='bg-neutral-40 p-x-3 p-y-2 h-100 of-y-auto d-f'>
          {task.attributes.customerId && (
            <CustomerDetails
              customerId={task.attributes.customerId}
              tenantId={taskTenantId}
              onBackClick={handleBackClick}
              displayName={task.displayName}
            />
          )}
          <TaskCustomerSearch
            taskId={taskId}
            taskTenantId={taskTenantId}
            show={!task.attributes.customerId}
          />
        </div>
      </TaskLoadingOverlay>
    </Stack>
  );
};

export default TaskView;
